import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllAccounts, deleteAccount } from "./AccountService";
import { apiFetch } from "../../api";
import { BASE_URL as u } from "../../../commonUrls";

const STMT_URL = `${u}/bank-statement`;
const CAT_URL  = `${u}/category`;
const MAX_FILE_MB = 5;

export default function AccountList() {
  const [accounts, setAccounts]         = useState([]);
  const [importAccount, setImportAccount] = useState(null);
  const [file, setFile]                 = useState(null);
  const [fileError, setFileError]       = useState(null);
  const [preview, setPreview]           = useState(null);
  const [checkedRefs, setCheckedRefs]   = useState(new Set());
  // overrides: { [sourceRef]: { categoryId, merchantId, merchantName } }
  const [overrides, setOverrides]       = useState({});
  const [categories, setCategories]     = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importing, setImporting]       = useState(false);
  const [result, setResult]             = useState(null);
  const fileRef = useRef();
  const nav = useNavigate();

  useEffect(() => { load(); }, []);

  const load = async () => {
    const data = await getAllAccounts();
    setAccounts(data);
  };

  const loadCategories = async () => {
    try {
      const res = await apiFetch(CAT_URL);
      if (res.ok) setCategories(await res.json());
    } catch (_) {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete account?")) return;
    await deleteAccount(id);
    load();
  };

  // ─── open / close ──────────────────────────────────────────
  const openImport = (account) => {
    setImportAccount({ id: account.id, accountName: account.accountName });
    resetModal();
    loadCategories();
  };

  const closeImport = () => {
    setImportAccount(null);
    resetModal();
  };

  const resetModal = () => {
    setFile(null);
    setFileError(null);
    setPreview(null);
    setCheckedRefs(new Set());
    setOverrides({});
    setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  // ─── file selection ────────────────────────────────────────
  const handleFileChange = (e) => {
    const f = e.target.files[0] || null;
    setPreview(null);
    setCheckedRefs(new Set());
    setOverrides({});
    setResult(null);
    setFileError(null);
    if (!f) { setFile(null); return; }
    const ext = f.name.toLowerCase().split(".").pop();
    if (!["csv","xls","xlsx","txt"].includes(ext)) {
      setFileError("Please select a bank statement file (.csv, .xls, .xlsx, .txt).");
      setFile(null); return;
    }
    if (f.size > MAX_FILE_MB * 1024 * 1024) {
      setFileError(`File too large (${(f.size/1024/1024).toFixed(1)} MB). Maximum is ${MAX_FILE_MB} MB.`);
      setFile(null); return;
    }
    setFile(f);
  };

  // ─── preview ───────────────────────────────────────────────
  const handlePreview = async () => {
    if (!file) return;
    setPreviewLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiFetch(
        `${STMT_URL}/preview?accountId=${encodeURIComponent(importAccount.id)}`,
        { method: "POST", body: fd }
      );
      const data = await res.json();
      setPreview(data);
      setCheckedRefs(new Set());
      // Auto-seed overrides from server suggestions
      const initial = {};
      (data.rows || []).forEach(row => {
        if (row.sourceRef && (row.suggestedCategoryId || row.suggestedMerchantId || row.merchantHint)) {
          initial[row.sourceRef] = {
            categoryId:   row.suggestedCategoryId || null,
            merchantId:   row.suggestedMerchantId || null,
            merchantName: row.merchantHint || null,
          };
        }
      });
      setOverrides(initial);
    } catch (err) {
      setPreview({ errors: [err.message] });
    } finally {
      setPreviewLoading(false);
    }
  };

  // ─── override helpers ──────────────────────────────────────
  const setRowCategory = (sourceRef, categoryId) => {
    setOverrides(prev => ({
      ...prev,
      [sourceRef]: { ...(prev[sourceRef] || {}), categoryId }
    }));
  };

  const setRowMerchant = (sourceRef, merchantName) => {
    setOverrides(prev => ({
      ...prev,
      [sourceRef]: { ...(prev[sourceRef] || {}), merchantId: null, merchantName }
    }));
  };

  const acceptAllSuggestions = () => {
    const updated = { ...overrides };
    (preview?.rows || []).forEach(row => {
      if (!row.sourceRef) return;
      if (row.suggestedCategoryId || row.merchantHint) {
        updated[row.sourceRef] = {
          categoryId:   row.suggestedCategoryId || null,
          merchantId:   row.suggestedMerchantId || null,
          merchantName: row.merchantHint || null,
        };
      }
    });
    setOverrides(updated);
  };

  const clearAllSuggestions = () => {
    const cleared = {};
    (preview?.rows || []).forEach(row => {
      if (row.sourceRef) cleared[row.sourceRef] = { categoryId: null, merchantId: null, merchantName: null };
    });
    setOverrides(cleared);
  };

  // ─── possible-duplicate toggles ───────────────────────────
  const toggleRef = (sourceRef) => {
    setCheckedRefs(prev => {
      const next = new Set(prev);
      next.has(sourceRef) ? next.delete(sourceRef) : next.add(sourceRef);
      return next;
    });
  };

  // ─── import ────────────────────────────────────────────────
  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const forceRefs = [...checkedRefs].join(",");
      // Only send non-empty overrides
      const cleanOverrides = {};
      Object.entries(overrides).forEach(([ref, ov]) => {
        if (ov.categoryId || ov.merchantId || ov.merchantName) {
          cleanOverrides[ref] = ov;
        }
      });
      fd.append("overridesJson", JSON.stringify(cleanOverrides));
      const res = await apiFetch(
        `${STMT_URL}/import?accountId=${encodeURIComponent(importAccount.id)}&forceImportRefs=${encodeURIComponent(forceRefs)}`,
        { method: "POST", body: fd }
      );
      const data = await res.json();
      setResult(data);
      load();
    } catch (err) {
      setResult({ errors: [err.message] });
    } finally {
      setImporting(false);
    }
  };

  // ─── derived state ─────────────────────────────────────────
  const rows          = preview?.rows ?? [];
  const newRows       = rows.filter(r => r.status === "NEW");
  const dupRows       = rows.filter(r => r.status === "DUPLICATE");
  const possibleDupRows = rows.filter(r => r.status === "POSSIBLE_DUPLICATE");
  const errorRows     = rows.filter(r => r.status === "ERROR");
  const totalToImport = newRows.length + checkedRefs.size;
  const hasValidationError = preview?.validationError;
  const hasData = rows.length > 0;
  const balanceDiffers = preview?.currentBalance != null && preview?.finalBalance != null &&
    Math.abs(Number(preview.currentBalance) - Number(preview.finalBalance)) > 0.01;

  // Enrichment summary counts (exclude duplicates / errors)
  const importableRows = rows.filter(r => r.status === "NEW" || r.status === "POSSIBLE_DUPLICATE");
  const enrichedCount  = importableRows.filter(r => r.enrichmentSource && r.enrichmentSource !== "NONE").length;
  const needsReview    = importableRows.filter(r => !r.enrichmentSource || r.enrichmentSource === "NONE").length;

  return (
    <div className="card">
      <button className="btn btn-primary" onClick={() => nav("new")}>+ Add Account</button>
      <div className="data-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Name</th><th>Type</th><th>Balance</th><th>Number</th><th>Comment</th><th>Active</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map(a => (
              <tr key={a.id}>
                <td>{a.accountName}</td>
                <td>{a.type}</td>
                <td>₹ {Number(a.currentBalance).toLocaleString("en-IN")}</td>
                <td>{a.accountNumber}</td>
                <td>{a.comment}</td>
                <td>{a.isActive ? "Yes" : "No"}</td>
                <td style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  <button className="btn" onClick={() => nav(`edit/${a.id}`)}>Edit</button>
                  <button className="btn btn-secondary" onClick={() => openImport(a)}>Import</button>
                  <button className="btn btn-danger" onClick={() => handleDelete(a.id)}>Del</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── IMPORT MODAL ──────────────────────────────────────── */}
      {importAccount && (
        <div style={overlay} onClick={closeImport}>
          <div style={modalBox} onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <div>
                <h3 style={{ margin: 0 }}>Import Bank Statement</h3>
                <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 2 }}>
                  Account: <strong>{importAccount.accountName}</strong>
                </div>
              </div>
              <button onClick={closeImport} style={closeBtn}>×</button>
            </div>

            {/* ── RESULT SCREEN ── */}
            {result && (
              <div>
                {result.validationError && <ErrorBox message={result.validationError} />}
                {!result.validationError && (
                  <>
                    <div style={{ textAlign: "center", marginBottom: 20 }}>
                      <div style={{ fontSize: 40, marginBottom: 6 }}>{result.importedCount > 0 ? "✓" : "ℹ"}</div>
                      <h4 style={{ margin: 0 }}>Import Complete</h4>
                    </div>
                    <div style={{ display: "flex", gap: 20, justifyContent: "center", flexWrap: "wrap", marginBottom: 18 }}>
                      <StatBlock label="Imported"    value={result.importedCount} color="#22c55e" />
                      <StatBlock label="Skipped"     value={result.skippedCount}  color="#6b7280" />
                      <StatBlock label="Errors"      value={result.errorCount}    color="#ef4444" />
                    </div>
                    {result.balanceSynced && result.balanceBefore != null && result.balanceAfter != null && (
                      <div style={{ ...alertBox, borderColor: "#6366f1", background: "rgba(99,102,241,0.08)", marginBottom: 12 }}>
                        <div style={{ color: "#818cf8", fontSize: 13 }}>
                          Balance corrected:{" "}
                          <strong>₹{Number(result.balanceBefore).toLocaleString("en-IN", {minimumFractionDigits: 2})}</strong>
                          {" → "}
                          <strong style={{ color: "#22c55e" }}>₹{Number(result.balanceAfter).toLocaleString("en-IN", {minimumFractionDigits: 2})}</strong>
                        </div>
                        {result.balanceSyncNote && (
                          <div style={{ color: "var(--text-muted)", fontSize: 11, marginTop: 4 }}>{result.balanceSyncNote}</div>
                        )}
                      </div>
                    )}
                    {result.phantomTransactionCount > 0 && (
                      <div style={{ ...alertBox, borderColor: "#f59e0b", background: "rgba(245,158,11,0.08)", marginBottom: 12 }}>
                        <strong style={{ color: "#fbbf24" }}>⚠ {result.phantomTransactionCount} manual transaction(s) in this period</strong>
                        <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 4 }}>
                          These were entered manually and are not in the statement. Review for possible duplicates.
                        </div>
                      </div>
                    )}
                  </>
                )}
                <div style={{ textAlign: "center", marginTop: 16 }}>
                  <button className="btn btn-primary" onClick={closeImport}>Done</button>
                </div>
              </div>
            )}

            {/* ── UPLOAD + PREVIEW SCREEN ── */}
            {!result && (
              <>
                <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 14 }}>
                  Upload a bank statement. The system will auto-detect the bank, flag duplicates,
                  and suggest categories for each transaction using rules and AI.
                </p>

                {/* File picker */}
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 4 }}>
                  <input ref={fileRef} type="file" accept=".csv,.xls,.xlsx,.txt" onChange={handleFileChange}
                    style={{ flex: 1, fontSize: 13 }} />
                  <button className="btn btn-secondary" onClick={handlePreview}
                    disabled={!file || previewLoading} style={{ whiteSpace: "nowrap" }}>
                    {previewLoading ? "Analysing…" : "Analyse"}
                  </button>
                </div>
                {fileError && <div style={{ color: "#f87171", fontSize: 12, marginBottom: 10 }}>{fileError}</div>}

                {hasValidationError && <ErrorBox message={preview.validationError} />}

                {!hasValidationError && preview?.errors?.length > 0 && (
                  <div style={{ ...alertBox, borderColor: "#f97316", background: "rgba(249,115,22,0.08)", marginBottom: 12 }}>
                    {preview.errors.map((e, i) => <div key={i} style={{ color: "#fb923c", fontSize: 12 }}>{e}</div>)}
                  </div>
                )}

                {/* Summary chips */}
                {hasData && !hasValidationError && (
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 14,
                    padding: "10px 14px", background: "rgba(255,255,255,0.04)", borderRadius: 8 }}>
                    <StatBlock label="New"          value={newRows.length}       color="#22c55e" />
                    <StatBlock label="Exact Dup."   value={dupRows.length}       color="#6b7280" />
                    <StatBlock label="Poss. Dup."   value={possibleDupRows.length} color="#f59e0b" />
                    <StatBlock label="Errors"       value={errorRows.length}     color="#ef4444" />
                    {enrichedCount > 0 && <StatBlock label="Auto-categorized" value={enrichedCount} color="#8b5cf6" />}
                    {needsReview > 0 && <StatBlock label="Need review" value={needsReview} color="#f97316" />}
                    {preview.finalBalance != null && (
                      <StatBlock label="Closing Balance"
                        value={`₹${Number(preview.finalBalance).toLocaleString("en-IN", {minimumFractionDigits:2})}`}
                        color="#6366f1" />
                    )}
                  </div>
                )}

                {/* Balance drift warning */}
                {balanceDiffers && !hasValidationError && (
                  <div style={{ ...alertBox, borderColor: "#f59e0b", background: "rgba(245,158,11,0.08)", marginBottom: 14 }}>
                    <strong style={{ color: "#fbbf24" }}>Balance will be corrected on import</strong>
                    <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 4 }}>
                      Current:&nbsp;<strong>₹{Number(preview.currentBalance).toLocaleString("en-IN", {minimumFractionDigits:2})}</strong>
                      &nbsp;→&nbsp;Statement:&nbsp;<strong>₹{Number(preview.finalBalance).toLocaleString("en-IN", {minimumFractionDigits:2})}</strong>
                    </div>
                  </div>
                )}

                {/* Enrichment controls */}
                {hasData && !hasValidationError && importableRows.length > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                    marginBottom: 10, padding: "8px 12px", background: "rgba(139,92,246,0.08)",
                    border: "1px solid rgba(139,92,246,0.25)", borderRadius: 8 }}>
                    <div style={{ fontSize: 13, color: "#a78bfa" }}>
                      {enrichedCount === importableRows.length
                        ? `All ${enrichedCount} transactions categorized`
                        : enrichedCount > 0
                          ? `${enrichedCount}/${importableRows.length} auto-categorized — ${needsReview} need review`
                          : `${importableRows.length} transactions need categorization`}
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="btn" style={{ fontSize: 11, padding: "3px 10px" }} onClick={acceptAllSuggestions}>
                        Accept all
                      </button>
                      <button className="btn" style={{ fontSize: 11, padding: "3px 10px" }} onClick={clearAllSuggestions}>
                        Clear all
                      </button>
                    </div>
                  </div>
                )}

                {/* POSSIBLE DUPLICATE section */}
                {possibleDupRows.length > 0 && !hasValidationError && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                      <div style={{ fontWeight: 600, color: "#f59e0b", fontSize: 14 }}>
                        ⚠ Possible Duplicates ({possibleDupRows.length})
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button className="btn" style={{ fontSize: 11, padding: "3px 8px" }}
                          onClick={() => setCheckedRefs(new Set(possibleDupRows.map(r => r.sourceRef)))}>
                          Include all
                        </button>
                        <button className="btn" style={{ fontSize: 11, padding: "3px 8px" }}
                          onClick={() => setCheckedRefs(new Set())}>
                          Skip all
                        </button>
                      </div>
                    </div>
                    <div style={{ border: "1px solid rgba(245,158,11,0.3)", borderRadius: 8, overflow: "hidden" }}>
                      {possibleDupRows.map((row, i) => (
                        <div key={i} style={{
                          display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px",
                          borderBottom: i < possibleDupRows.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none",
                          background: checkedRefs.has(row.sourceRef) ? "rgba(245,158,11,0.06)" : "transparent"
                        }}>
                          <input type="checkbox" checked={checkedRefs.has(row.sourceRef)}
                            onChange={() => toggleRef(row.sourceRef)}
                            style={{ marginTop: 3, flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "baseline" }}>
                              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{row.rawDate}</span>
                              <span style={{ fontSize: 13, fontWeight: 500 }} title={row.particulars}>
                                {truncate(row.particulars, 50)}
                              </span>
                              <AmountChip row={row} />
                            </div>
                            {row.duplicateHint && <div style={{ color: "#f59e0b", fontSize: 11, marginTop: 2 }}>⚠ {row.duplicateHint}</div>}
                            <EnrichmentRow row={row} overrides={overrides} categories={categories}
                              setRowCategory={setRowCategory} setRowMerchant={setRowMerchant} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Main transaction table */}
                {hasData && !hasValidationError && (
                  <div style={{ maxHeight: 380, overflowY: "auto", marginBottom: 16 }}>
                    <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ background: "rgba(255,255,255,0.05)", position: "sticky", top: 0 }}>
                          <th style={th}>Date</th>
                          <th style={th}>Particulars</th>
                          <th style={th}>Type</th>
                          <th style={{ ...th, textAlign: "right" }}>Amount</th>
                          <th style={th}>Category / Merchant</th>
                          <th style={th}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.filter(r => r.status !== "POSSIBLE_DUPLICATE").map((row, i) => (
                          <tr key={i} style={{
                            borderBottom: "1px solid rgba(255,255,255,0.05)",
                            opacity: row.status === "DUPLICATE" ? 0.45 : 1
                          }}>
                            <td style={td}>{row.rawDate}</td>
                            <td style={{ ...td, maxWidth: 200 }}>
                              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                title={row.particulars}>{row.particulars || "—"}</div>
                            </td>
                            <td style={td}>
                              {row.type && (
                                <span style={{ color: row.type === "INCOME" ? "#22c55e" : "#f87171", fontWeight: 600 }}>
                                  {row.type === "INCOME" ? "CR" : "DR"}
                                </span>
                              )}
                            </td>
                            <td style={{ ...td, textAlign: "right" }}>
                              {row.amount != null ? `₹${Number(row.amount).toLocaleString("en-IN", {minimumFractionDigits:2})}` : "—"}
                            </td>
                            <td style={{ ...td, minWidth: 160 }}>
                              {row.status !== "DUPLICATE" && row.status !== "ERROR" ? (
                                <EnrichmentRow row={row} overrides={overrides} categories={categories}
                                  setRowCategory={setRowCategory} setRowMerchant={setRowMerchant} />
                              ) : "—"}
                            </td>
                            <td style={td}><StatusBadge row={row} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Footer */}
                {hasData && !hasValidationError && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                    paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                    <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
                      {totalToImport === 0 ? "Nothing new to import." : (
                        <>Will import <strong style={{ color: "#22c55e" }}>{totalToImport}</strong> transaction{totalToImport !== 1 ? "s" : ""}
                          {checkedRefs.size > 0 && <> (incl. <strong style={{ color: "#f59e0b" }}>{checkedRefs.size}</strong> poss. dup{checkedRefs.size !== 1 ? "s" : ""})</>}.</>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="btn" onClick={closeImport}>Cancel</button>
                      {totalToImport > 0 && (
                        <button className="btn btn-primary" onClick={handleImport} disabled={importing}>
                          {importing ? "Importing…" : `Import ${totalToImport}`}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Enrichment inline row ────────────────────────────────────────────────────

function EnrichmentRow({ row, overrides, categories, setRowCategory, setRowMerchant }) {
  const ov = overrides[row.sourceRef] || {};
  const currentCatId    = ov.categoryId !== undefined ? ov.categoryId : row.suggestedCategoryId;
  const currentMerchant = ov.merchantName !== undefined ? ov.merchantName
                        : (row.merchantHint || "");
  const [editingMerchant, setEditingMerchant] = useState(false);
  const [merchantInput, setMerchantInput] = useState(currentMerchant);

  const sourceColor = {
    MERCHANT_MATCH: "#22c55e",
    RULE:           "#f59e0b",
    AI:             "#8b5cf6",
    NONE:           "#6b7280",
  }[row.enrichmentSource] || "#6b7280";

  const sourceLabel = {
    MERCHANT_MATCH: "Matched",
    RULE:           "Rule",
    AI:             "AI",
    NONE:           "—",
  }[row.enrichmentSource] || "—";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 2 }}>
      {/* Merchant name */}
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {editingMerchant ? (
          <input
            value={merchantInput}
            onChange={e => setMerchantInput(e.target.value)}
            onBlur={() => { setEditingMerchant(false); setRowMerchant(row.sourceRef, merchantInput); }}
            onKeyDown={e => { if (e.key === "Enter") { setEditingMerchant(false); setRowMerchant(row.sourceRef, merchantInput); } }}
            style={{ fontSize: 11, padding: "1px 4px", borderRadius: 4, border: "1px solid #6366f1",
              background: "transparent", color: "inherit", width: 120 }}
            autoFocus
          />
        ) : (
          <span
            onClick={() => { setMerchantInput(currentMerchant); setEditingMerchant(true); }}
            title="Click to edit merchant name"
            style={{ fontSize: 11, color: currentMerchant ? "#94a3b8" : "#4b5563",
              cursor: "pointer", borderBottom: "1px dashed #4b5563", whiteSpace: "nowrap",
              overflow: "hidden", textOverflow: "ellipsis", maxWidth: 130 }}>
            {currentMerchant || "Set merchant…"}
          </span>
        )}
        {row.enrichmentSource && row.enrichmentSource !== "NONE" && (
          <span style={{ fontSize: 9, color: sourceColor, fontWeight: 700,
            background: sourceColor + "22", padding: "1px 4px", borderRadius: 4, flexShrink: 0 }}>
            {sourceLabel}
          </span>
        )}
      </div>
      {/* Category dropdown */}
      <select
        value={currentCatId || ""}
        onChange={e => setRowCategory(row.sourceRef, e.target.value || null)}
        style={{ fontSize: 11, padding: "2px 4px", borderRadius: 4,
          border: "1px solid rgba(255,255,255,0.12)", background: "var(--surface, #1e1e2e)",
          color: currentCatId ? "inherit" : "#6b7280", maxWidth: 160 }}>
        <option value="">— No category —</option>
        {categories.map(c => (
          <option key={c.id} value={c.id}>{c.categoryName}</option>
        ))}
      </select>
    </div>
  );
}

// ── Small reusable components ─────────────────────────────────────────────────

function AmountChip({ row }) {
  const isIncome = row.type === "INCOME";
  return (
    <span style={{ fontWeight: 700, color: isIncome ? "#22c55e" : "#f87171", whiteSpace: "nowrap" }}>
      {isIncome ? "+" : "−"}₹{Number(row.amount).toLocaleString("en-IN", {minimumFractionDigits: 2})}
    </span>
  );
}

function ErrorBox({ message }) {
  return (
    <div style={{ ...alertBox, borderColor: "#ef4444", background: "rgba(239,68,68,0.08)", marginBottom: 14 }}>
      <strong style={{ color: "#f87171" }}>Error</strong>
      <div style={{ color: "#fca5a5", fontSize: 13, marginTop: 4 }}>{message}</div>
    </div>
  );
}

function StatBlock({ label, value, color }) {
  return (
    <div style={{ textAlign: "center", minWidth: 52 }}>
      <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 10, color: "var(--text-muted)", whiteSpace: "nowrap" }}>{label}</div>
    </div>
  );
}

function StatusBadge({ row }) {
  const map = {
    NEW:                { bg: "rgba(34,197,94,0.15)",   color: "#22c55e", label: "New"       },
    DUPLICATE:          { bg: "rgba(107,114,128,0.15)", color: "#9ca3af", label: "Exact Dup" },
    POSSIBLE_DUPLICATE: { bg: "rgba(245,158,11,0.15)",  color: "#f59e0b", label: "Poss. Dup" },
    ERROR:              { bg: "rgba(239,68,68,0.15)",   color: "#ef4444", label: "Error"     },
    IMPORTED:           { bg: "rgba(99,102,241,0.15)",  color: "#818cf8", label: "Imported"  },
    SKIPPED:            { bg: "rgba(107,114,128,0.15)", color: "#9ca3af", label: "Skipped"   },
  };
  const s = map[row?.status] || { bg: "transparent", color: "#9ca3af", label: row?.status ?? "—" };
  return (
    <div>
      <span style={{ background: s.bg, color: s.color, padding: "2px 7px",
        borderRadius: 10, fontSize: 10, fontWeight: 600 }}>{s.label}</span>
      {row.parseError && (
        <div style={{ color: "#f87171", fontSize: 10, marginTop: 2 }} title={row.parseError}>
          {truncate(row.parseError, 40)}
        </div>
      )}
    </div>
  );
}

function truncate(s, max) {
  if (!s) return "";
  return s.length <= max ? s : s.substring(0, max) + "…";
}

// ─── style constants ─────────────────────────────────────────
const overlay  = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const modalBox = { background: "var(--surface, #1e1e2e)",
  border: "1px solid var(--border, rgba(255,255,255,0.12))",
  borderRadius: 12, padding: 24, maxWidth: 900, width: "96vw", maxHeight: "93vh", overflowY: "auto" };
const alertBox = { border: "1px solid", borderRadius: 8, padding: "10px 14px" };
const closeBtn = { background: "none", border: "none", fontSize: 22, cursor: "pointer",
  color: "var(--text-muted)", lineHeight: 1 };
const th = { padding: "7px 8px", textAlign: "left", fontWeight: 600, fontSize: 11,
  color: "var(--text-muted)", whiteSpace: "nowrap" };
const td = { padding: "5px 8px", verticalAlign: "top" };
